import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/lib/api/client';

// ── Shared Dev-tools chip bar (#73) ─────────────────────────────────────────
// The dev-dashboard tab chips + an Architecture chip, rendered above the
// breadcrumbs. Used on both the dev dashboard and the Architecture Map page so
// you can jump between them from one bar.

export type DevBarId =
  | 'overview' | 'tasks' | 'features' | 'portal'
  | 'audit' | 'desktop' | 'changelog' | 'about' | 'architecture';

export const DEV_TABS: { id: DevBarId; label: string; icon: string }[] = [
  { id: 'overview',     label: 'Overview',          icon: 'dashboard' },
  { id: 'tasks',        label: 'Task Queue',        icon: 'task_alt' },
  { id: 'features',     label: 'Feature Inventory', icon: 'account_tree' },
  { id: 'portal',       label: 'Portal Preview',    icon: 'open_in_new' },
  { id: 'audit',        label: 'Security Audit',    icon: 'security' },
  { id: 'desktop',      label: 'Desktop (Preview)', icon: 'desktop_windows' },
  { id: 'changelog',    label: 'Changelog',         icon: 'history' },
  { id: 'about',        label: 'About',             icon: 'layers' },
  { id: 'architecture', label: 'Architecture Map',  icon: 'schema' },
];

interface Props {
  active: DevBarId | null;
  badges?: Partial<Record<DevBarId, number>>;
  // On the dev dashboard, tab chips toggle in-place; pass a handler.
  // When omitted (e.g. the Architecture page), tab chips navigate to /dev.
  onTab?: (id: DevBarId) => void;
}

export function DevToolsBar({ active, badges, onTab }: Props) {
  const navigate = useNavigate();
  // CR #124: the Task Queue chip always flags the live queue state — red count
  // for fulfill-now items, amber for the rest of the open/in-progress pile.
  const [queueFlags, setQueueFlags] = useState<{ urgent: number; open: number }>({ urgent: 0, open: 0 });
  useEffect(() => {
    apiClient.get('/dev/change-requests')
      .then(r => {
        const rows = (r.data.data ?? []) as { status: string; fulfill_now?: number }[];
        const live = rows.filter(x => ['open', 'in_progress', 'proposed'].includes(x.status));
        setQueueFlags({
          urgent: live.filter(x => x.fulfill_now).length,
          open: live.filter(x => !x.fulfill_now).length,
        });
      })
      .catch(() => {});
  }, []);

  const pick = (id: DevBarId) => {
    if (id === 'architecture') { navigate('/dev/architecture'); return; }
    if (onTab) onTab(id);
    else navigate(`/dev?tab=${id}`);
  };

  return (
    <div data-cr-ui className="flex items-center gap-1 px-3 py-2 border-b border-gray-200 bg-white overflow-x-auto scrollbar-none">
      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 pr-1 flex-shrink-0">Dev tools</span>
      {DEV_TABS.map(t => (
        <button
          key={t.id}
          onClick={() => pick(t.id)}
          className={`flex items-center gap-1.5 h-8 px-2.5 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap flex-shrink-0 ${
            active === t.id ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-500 border-gray-200 hover:border-[#C77D3F]/40'
          }`}
        >
          <span className="material-symbols-outlined text-base">{t.icon}</span>
          {t.label}
          {t.id === 'tasks' && queueFlags.urgent > 0 && (
            <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-500 text-white" title={`${queueFlags.urgent} flagged fulfill-now`}>
              {queueFlags.urgent}
            </span>
          )}
          {t.id === 'tasks' && queueFlags.open > 0 && (
            <span className={`ml-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full ${active === t.id ? 'bg-white/25 text-white' : 'bg-amber-400 text-amber-950'}`} title={`${queueFlags.open} open`}>
              {queueFlags.open}
            </span>
          )}
          {badges?.[t.id] ? (
            <span className={`ml-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full ${active === t.id ? 'bg-white/25 text-white' : 'bg-green-500 text-white'}`}>{badges[t.id]}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}
